import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';

/**
 * Inline upgrade panel rendered on any page whose data query returns 402
 * (check.feature middleware rejected the request). Replaces the previous
 * global "402 → /profile" redirect so users can see WHAT they're missing
 * before being asked to upgrade.
 *
 * Props:
 *   - feature   string  (the feature key, e.g. "hardware_control")
 *                       used both in the body copy and for analytics later.
 *   - title     string  (optional override, defaults to a generic message)
 *   - message   string  (optional, from the 402 payload if available)
 */
export default function LegacyFeatureLocked({ feature, title, message }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="p-4">
      <div
        className="card text-center mx-auto p-5"
        style={{
          maxWidth: 540,
          borderRadius: 12,
        }}
      >
        <div
          className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: 64, height: 64,
            background: 'linear-gradient(135deg, #5b8def 0%, #8b5cf6 100%)',
            color: '#fff',
            fontSize: 28,
          }}
        >
          <i className="pi pi-lock" />
        </div>

        <h3 className="fw-bold mb-2">
          {title ?? t('feature_locked.title', 'Upgrade your plan to access this feature')}
        </h3>

        <p className="text-muted mb-4 small">
          {message ?? t(
            'feature_locked.body',
            "This feature isn't included in your current plan. Upgrade to unlock it."
          )}
        </p>

        {feature && (
          <p className="text-muted small mb-4">
            <i className="pi pi-tag me-1" />
            <code>{feature}</code>
          </p>
        )}

        <div className="d-flex justify-content-center gap-2 flex-wrap">
          <Button
            label={t('feature_locked.upgrade', 'View Plans')}
            icon="pi pi-arrow-up"
            onClick={() => navigate('/profile', { state: { activeTab: 'billing' } })}
          />
          <Button
            label={t('feature_locked.back', 'Back to Dashboard')}
            severity="secondary"
            outlined
            onClick={() => navigate('/dashboard')}
          />
        </div>
      </div>
    </div>
  );
}
